import { Info, Share2, Palette } from "lucide-react";
import type { MapStyleInspectorWorkspaceProps } from "../../contracts/map-styles";
import { cn } from "../../lib/utils";
import { MapAssetStatList } from "../map-assets/MapAssetStatList";

/**
 * The top-level workspace for inspecting a map style.
 * Composes style dependencies, map preview, and runtime diagnostics into a stable three-region layout.
 */
export function MapStyleInspectorWorkspace({
  title,
  subtitle,
  styleIdLabel,
  shareScopeLabel,
  summaryItems = [],
  contextPanel,
  previewPanel,
  diagnosticsPanel,
  className,
}: MapStyleInspectorWorkspaceProps) {
  return (
    <div className={cn("flex flex-col h-full bg-background overflow-hidden", className)}>
      {/* Workspace Header */}
      <div className="flex flex-col gap-4 p-4 border-b border-border bg-muted/5">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex flex-col gap-1 min-w-0">
            <h2 className="text-lg font-black tracking-tight text-foreground uppercase truncate">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
                {subtitle}
              </p>
            )}
            
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              {styleIdLabel && (
                <div className="flex items-center gap-1.5">
                  <Palette className="h-3 w-3 text-muted-foreground/60" />
                  <span className="text-[10px] font-mono font-bold text-muted-foreground/80">
                    {styleIdLabel}
                  </span>
                </div>
              )}
              {shareScopeLabel && (
                <div className="flex items-center gap-1.5">
                  <Share2 className="h-3 w-3 text-muted-foreground/60" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
                    {shareScopeLabel}
                  </span>
                </div>
              )}
            </div>
          </div>

          {summaryItems && summaryItems.length > 0 && (
            <div className="shrink-0 flex items-center p-3 rounded-lg bg-background border border-border shadow-sm">
              <MapAssetStatList items={summaryItems} />
            </div>
          )}
        </div>
      </div>

      {/* Three-Region Body */}
      <div className="flex-1 flex min-h-0 divide-x divide-border">
        {/* Region 1: Context (Left) - Typically Dependencies/Metadata */}
        <div className="w-[320px] shrink-0 flex flex-col bg-muted/5 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-3">
            {contextPanel}
          </div>
          <div className="p-3 border-t border-border bg-muted/10">
            <div className="flex items-center gap-2 text-muted-foreground/60">
              <Info className="h-3.5 w-3.5" />
              <p className="text-[10px] font-medium leading-tight italic">
                Inspect dependencies and baseline style configuration
              </p>
            </div>
          </div>
        </div>

        {/* Region 2: Map Preview (Center) */}
        <div className="flex-1 flex flex-col bg-muted/10 overflow-hidden p-3">
          {previewPanel}
        </div>

        {/* Region 3: Diagnostics (Right) */}
        <div className="w-[380px] shrink-0 flex flex-col bg-muted/5 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-3">
            {diagnosticsPanel}
          </div>
        </div>
      </div>
    </div>
  );
}
